<mat-card class="detail-card" valumizeCardContextMenu [commentSlug]="'asset-developments-since-investment'">
  <mat-card-header class="detail-card-header">
    <mat-card-title data-test="title">Developments Since Investment (VCP progress)</mat-card-title>
    <valumize-card-comments-button [commentSlug]="'asset-developments-since-investment'"></valumize-card-comments-button>
  </mat-card-header>
  <!-- Declared once at card level: the actions footer below is a sibling of the content div, so
       @lets scoped to that div could not be reused there and the observables got re-subscribed. -->
  @let reportId = (relatedReportId$ | async);
  @let currentReportDate = (reportDate$ | async)?.date;
  @let currentDevelopments = (developmentsSinceInvestment$ | async);
  <!-- This editor's own save state, not the shared fund-investment status: bound to the latter, this
       card announced "successfully saved" whenever any other editor on the page saved. -->
  @if (saveState$ | async; as saveState) {
    <mat-card-subtitle>
      @if (saveState.status === 'ERROR') {
        <valumize-info-panel [appearance]="'ERROR'" [message]="saveState.errorMessage"></valumize-info-panel>
      }
      @if (saveState.status === 'SAVED') {
        <valumize-info-panel [appearance]="'SAVED'" [message]="'Developments Since Investment successfully saved'"></valumize-info-panel>
      }
      @if (saveState.status === 'LOADING') {
        <mat-progress-spinner></mat-progress-spinner>
      }
    </mat-card-subtitle>
  }
  <div class="detail-card-content" [formGroup]="developmentsSinceInvestmentForm">
    @if (commentaryHistory$ | async; as history) {
      @if (history.status === 'LOADING') {
        <mat-progress-spinner></mat-progress-spinner>
      } @else if (history.status === 'ERROR') {
        <valumize-info-panel [appearance]="'ERROR'" [message]="history.errorMessage"></valumize-info-panel>
      } @else if (history.data.length === 0 && !reportId) {
        <!-- @else if, not a sibling @if: gated only on an empty array this asserted "never been part
             of a fund report" on first paint, while the request was in flight, and again next to the
             error panel after it failed. The !reportId guard keeps it from contradicting the editor
             below, which renders whenever a report is in context. -->
        <div class="no-text" style="color: grey">No developments recorded yet — this asset has never been part of a fund report.</div>
      }
      <div class="developments-since-investment-history">
        <!-- The current report's entry renders from the fundInvestment slice, independent of the
             history read-model. It used to live inside the @for below, so a failed history GET left
             the card read-only with no way to enter or correct commentary short of a page reload. -->
        @if (reportId) {
          <div class="key-value-pair-column" data-test="current-report-entry">
            <!-- Label left, modification date and actions right. The date belongs on this row rather
                 than in a card footer: it is this record's modDate, not the card's. Kept identical to
                 the Financial History card's Commentary tab, where a footer inside the tab body also
                 caused a scrollbar. -->
            <div class="key-value-pair-column-title title-with-actions">
              <span>
                Recorded on Report Date: @if (currentReportDate) {
                  {{ currentReportDate | date }}
                } @else {
                  <span style="color: grey">not stated</span>
                }
              </span>
              <span class="row-actions developments-since-investment-actions">
                <!-- reportId is guaranteed here: this whole block is inside @if (reportId). -->
                <span class="last-modified" data-test="modification-date">Last modified: {{ currentDevelopments?.modDate | timestamp }}</span>
                @if (!isEditable) {
                  <button (click)="editMode()" [disabled]="(isEditDisabled$ | async)!" color="primary" data-test="edit-button" mat-icon-button>
                    <mat-icon>edit</mat-icon>
                  </button>
                }
                @if (isEditable) {
                  <button (click)="save()" [disabled]="developmentsSinceInvestmentForm.invalid" color="primary" data-test="save-button" mat-icon-button>
                    <mat-icon>save</mat-icon>
                  </button>
                  <button (click)="cancel()" color="primary" data-test="cancel-button" mat-icon-button>
                    <mat-icon>cancel</mat-icon>
                  </button>
                }
              </span>
            </div>
            @if (isEditable) {
              <mat-form-field class="description">
                <textarea class="line-breaks" formControlName="text" data-test="text" matInput></textarea>
              </mat-form-field>
            } @else if (currentDevelopments?.text) {
              <div class="line-breaks">{{ currentDevelopments?.text }}</div>
            } @else {
              <div class="no-text" style="color: grey">No developments recorded yet.</div>
            }
          </div>
        }
        @for (entry of history.data; track entry.fundReportId) {
          @if (entry.fundReportId !== reportId) {
            <div class="key-value-pair-column">
              <div class="key-value-pair-column-title">
                <!-- An undated report (imported without a parseable report date) would otherwise render
                     "Recorded on Report Date:" followed by nothing. -->
                Recorded on Report Date: @if (entry.reportDate) {
                  {{ entry.reportDate | date }}
                } @else {
                  <span style="color: grey">not stated</span>
                }
              </div>
              @if (entry.developmentsSinceInvestment.text) {
                <div class="line-breaks">{{ entry.developmentsSinceInvestment.text }}</div>
              } @else {
                <div class="no-text" style="color: grey">No developments recorded yet.</div>
              }
            </div>
          }
        }
      </div>
    }
  </div>
</mat-card>
